/**
 * Document action hooks for fork and clone operations
 *
 * These hooks encapsulate the navigation logic for forking and cloning documents,
 * separating business logic from UI rendering in menu item components.
 */
import {cloneDocumentRoute, forkDocumentRoute, Origin} from "@buildwithflux/core";
import {DocumentUid, EnterpriseUid, OrganizationUid} from "@buildwithflux/models";
import {isCypressTestUserAgent} from "@buildwithflux/shared";
import {useCallback} from "react";
import {useHistory} from "react-router-dom";

/**
 * Hook to get the fork document action
 *
 * Returns a function that navigates to the fork document route.
 * Uses window.open for normal users, history.push for Cypress tests.
 *
 * @returns A function to execute the fork document action
 */
export function useForkDocumentAction(): (
    documentUid: DocumentUid,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
) => void {
    const history = useHistory();

    return useCallback(
        (
            documentUid: DocumentUid,
            organizationUid: OrganizationUid | undefined,
            enterpriseUid: EnterpriseUid | undefined,
        ) => {
            const route = forkDocumentRoute(documentUid, Origin.Menu, organizationUid, enterpriseUid);

            if (isCypressTestUserAgent()) {
                history.push(route);
            } else {
                window.open(route, "_blank");
            }
        },
        [history],
    );
}

/**
 * Hook to get the clone document action
 *
 * Returns a function that navigates to the clone document route.
 * Uses window.open for normal users, history.push for Cypress tests.
 *
 * @returns A function to execute the clone document action
 */
export function useCloneDocumentAction(): (
    documentUid: DocumentUid,
    organizationUid: OrganizationUid | undefined,
    enterpriseUid: EnterpriseUid | undefined,
) => void {
    const history = useHistory();

    return useCallback(
        (
            documentUid: DocumentUid,
            organizationUid: OrganizationUid | undefined,
            enterpriseUid: EnterpriseUid | undefined,
        ) => {
            const route = cloneDocumentRoute(documentUid, Origin.Menu, organizationUid, enterpriseUid);

            if (isCypressTestUserAgent()) {
                history.push(route);
            } else {
                window.open(route, "_blank");
            }
        },
        [history],
    );
}
